import { useState } from 'react';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { ParentAction, PaymentRow, approveIdentification } from '../services/billingApi';
import SelectedInvoicesSummary from './SelectedInvoicesSummary';

interface Props {
    payment: PaymentRow;
    invoices: any[];
    /**
     * A cobrança anterior escolhida na aprovação (AF-4062), só das faturas em que a aprovação
     * decide. As demais vão só pelo id: a escolha delas é a da marcação.
     */
    choices?: Record<string, ParentAction | ''>;
    onClose: () => void;
    onApproved: () => void;
}

const ApproveInvoicesDialog: React.FC<Props> = ({
    payment, invoices, choices = {}, onClose, onApproved,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const several = invoices.length > 1;

    const confirm = async () => {
        setSubmitting(true);
        try {
            await approveIdentification(payment.paymentId, invoices.map(inv => (
                inv.invoiceId in choices
                    ? { invoice_id: inv.invoiceId, parent_action: choices[inv.invoiceId] || null }
                    : inv.invoiceId
            )));
            toast.success(several
                ? `Identificação aprovada. ${invoices.length} faturas foram liquidadas.`
                : 'Identificação aprovada. A fatura foi liquidada.');
            onApproved();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível aprovar: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div role="dialog" aria-label="Aprovar identificação"
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaCheckCircle className="text-green-600" />
                        {several ? `Aprovar ${invoices.length} faturas` : 'Aprovar esta fatura'}
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 space-y-3">
                    <SelectedInvoicesSummary payment={payment} invoices={invoices} />
                    <p className="text-sm px-3 py-2 rounded bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        {several ? 'Estas faturas serão liquidadas' : 'Esta fatura será liquidada'} com este
                        pagamento, e o settlement movimenta os títulos. Depois disso não há reprovação:
                        o caminho passa a ser o estorno.
                    </p>
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={confirm} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
                        {submitting ? 'Aprovando...' : 'Aprovar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApproveInvoicesDialog;
